import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { CircularProgress, Box, Typography } from '@mui/material';
import { useState, useEffect } from 'react';

export const ProtectedRoute = ({ children, allowedRoles = ['client', 'admin'] }) => {
  const { user, loading } = useAuth();
  const [roleLoadingGracePeriod, setRoleLoadingGracePeriod] = useState(false);
  const location = useLocation();

  console.log('[ProtectedRoute] Auth state:', { 
    loading, 
    user: user ? { id: user.id, email: user.email, role: user.role } : null, 
    userRole: user?.role,
    allowedRoles,
    currentPath: location.pathname
  });

  // Give a longer grace period for role loading after user is set
  useEffect(() => {
    if (user && !user.role && !loading) {
      console.log('[ProtectedRoute] User found without role, starting 5-second grace period for path:', location.pathname);
      setRoleLoadingGracePeriod(true);
      
      // Give 5 seconds for role to load after user is set
      const timeout = setTimeout(() => {
        console.log('[ProtectedRoute] Grace period ended for path:', location.pathname);
        setRoleLoadingGracePeriod(false);
      }, 5000);
      
      return () => clearTimeout(timeout);
    } else {
      if (roleLoadingGracePeriod) {
        console.log('[ProtectedRoute] Ending grace period - user now has role or no user for path:', location.pathname);
      }
      setRoleLoadingGracePeriod(false);
    }
  }, [user, loading, roleLoadingGracePeriod, location.pathname]);

  // If we're still loading auth state OR in role loading grace period, show spinner
  if (loading || roleLoadingGracePeriod) {
    console.log('[ProtectedRoute] Still loading auth state or waiting for role', { loading, roleLoadingGracePeriod, path: location.pathname });
    return (
      <Box 
        display="flex" 
        flexDirection="column"
        justifyContent="center" 
        alignItems="center" 
        minHeight="100vh"
        gap={2}
      >
        <CircularProgress style={{ color: '#8A42EC' }} />
        <Typography variant="body2" color="textSecondary">
          {loading ? 'Loading your dashboard...' : 'Verifying permissions...'}
        </Typography>
      </Box>
    );
  }

  // If there's no user at all, redirect to login
  if (!user) {
    console.log('[ProtectedRoute] No user, redirecting to login from path:', location.pathname);
    return <Navigate to="/login" replace />;
  }

  // SPECIAL CASE: If client user is on an admin route, redirect them to client area
  if (user.role === 'client' && (location.pathname.startsWith('/admin') || location.pathname.startsWith('/contracts') || location.pathname.startsWith('/performance') || location.pathname.startsWith('/points-dashboard') || location.pathname.startsWith('/transactions') || location.pathname.startsWith('/forecast') || location.pathname.startsWith('/audit') || location.pathname.startsWith('/users') || location.pathname.startsWith('/syncing'))) {
    console.log('[ProtectedRoute] REDIRECT FIX: Client user found on admin route:', location.pathname, '- redirecting to /client');
    return <Navigate to="/client" replace />;
  }

  // Use the actual user role value, don't default to anything
  const userRole = user.role;
  
  // Allow access if:
  // 1. No specific roles are required (allowedRoles is empty)
  // 2. User role is one of the allowed roles
  const hasAccess = allowedRoles.length === 0 || (userRole && allowedRoles.includes(userRole));

  console.log('[ProtectedRoute] Access check:', {
    userRole,
    allowedRoles,
    hasAccess,
    userEmail: user.email,
    currentPath: location.pathname
  });

  if (!hasAccess) {
    console.log('[ProtectedRoute] DENIED ACCESS: User role:', userRole, 'cannot access path:', location.pathname, 'which requires roles:', allowedRoles);
    return <Navigate to="/unauthorized" replace />;
  }

  console.log('[ProtectedRoute] Access granted for user:', user.email, 'with role:', userRole, 'to path:', location.pathname);
  return children;
}; 